<template>
    <section id="sectionPrincipal" class="catalog-forecast-dashboard">
        <Loader :loading="loading" :fixed="true"></Loader>

        <div class="row d-flex align-items-center catalog-forecast-dashboard-header mb-5">
            <div class="col-auto" @click="updateOffset(-1)" v-if="periods.length > 8" :class="{ 'disable-arrow': offsetControl === 0 }">
                <span class="control"><i class="fa fa-arrow-left fa-lg"></i></span>
            </div>
            <div class="col" v-for="(period, index) in periods" :key="index" v-if="index >= offsetControl && index < offsetControl + 8">
                <span :class="(selectedPeriod.id === period.id ? 'active' : '') + (period.isOpened ? ' open ' : ' ')" @click="changePeriod(period)">
                    {{ getDate(period.stampCode, "MMM YY", "month") }}
                </span>
            </div>
            <div
                class="col-auto"
                @click="updateOffset(1)"
                v-if="periods.length > 8"
                :class="{ 'disable-arrow': offsetControl === periods.length - 8 }"
            >
                <span class="control"><i class="fa fa-arrow-right fa-lg"></i></span>
            </div>
        </div>

        <div class="catalog-forecast-dashboard-title">
            <span>{{ $t("catalogForecast.title") }}</span>
        </div>

        <div v-if="selectedPeriod">
            <span class="catalog-forecast-dashboard-period-title">{{ periodTitle }}</span>

            <div class="mt-5 row d-flex align-items-start catalog-forecast-dashboard-recap" v-if="zoneGroupsFiltered.length">
                <div class="col"><PeriodDates :period="selectedPeriod"></PeriodDates></div>
                <div class="col px-5 text-global-progress">
                    <div class="row">
                        <div class="col-xl-2"></div>
                        <div class="col">
                            <div class="row mb-2">
                                <div class="col">
                                    <span>{{ $t("catalogForecast.globalProgression") }} :</span>
                                </div>
                                <div class="col-auto">
                                    <span>{{ getGlobalValidatePercent() }}%</span>
                                </div>
                            </div>
                            <VmProgress
                                :class="
                                    (isLineProgress ? 'line-progress ' : 'circle-progress ') +
                                        (getGlobalValidatePercent() === 0 ? ' zero-percent' : '')
                                "
                                :percentage="getGlobalValidatePercent()"
                                :show-text="false"
                                :type="isLineProgress ? 'line' : 'circle'"
                                :stroke-linecap="'square'"
                                :stroke-width="20"
                                stroke-color="#747795"
                                track-color="white"
                            />
                        </div>
                        <div class="col-xl-2"></div>
                    </div>
                </div>
                <div class="col-auto" v-if="canRechallenge">
                    <ClientColorButton
                        :rounded="true"
                        background-color="#F9C53C"
                        color="#fff"
                        class="btn-global-rechallenge mt-3"
                        @click="openModalAllRechallengeMarkets()"
                    >
                        <span><i class="fa fa-undo-alt fa-lg"></i></span> <span class="ml-3">{{ $t("catalogForecast.rechallengeMarkets") }}</span>
                    </ClientColorButton>
                </div>
            </div>

            <div
                class="mt-5 row"
                v-if="zoneGroupsFiltered.length"
                v-masonry="'containerId'"
                :item-selector="'.item'"
                :origin-left="false"
                :horizontal-order="true"
            >
                <div class="col-12 col-md-4 catalog-forecast-dashboard-item item pb-5" v-for="zg in zoneGroupsFiltered" :key="zg.id" v-masonry-tile>
                    <div class="dashboard-header">
                        <div class="row d-flex align-items-center">
                            <div class="col-auto">
                                <ClientColorButton
                                    v-if="canRechallenge"
                                    size="m"
                                    :rounded100="true"
                                    class="ml-4"
                                    background-color="#F9C53C"
                                    color="#fff"
                                    @click="openModalRechallengeMarkets(zg.brandZonesDashboard)"
                                >
                                    <i class="fa fa-undo-alt fa-lg"></i>
                                </ClientColorButton>
                                <p v-if="!canRechallenge" class="m-0" style="min-width: 32px"></p>
                            </div>
                            <div class="col d-flex align-items-end justify-content-center">
                                <span>{{ zg.name }}</span>
                            </div>
                            <div class="col-auto">
                                <span>{{ getValidatedNb(zg.brandZonesDashboard) }}/{{ zg.brandZonesDashboard.length }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="dashboard-lines mt-4 mb-4">
                        <div
                            class="dashboard-line d-flex align-items-center pointer"
                            v-for="brandZone in zg.brandZonesDashboard"
                            :key="brandZone.zone.id"
                            @click="$router.push({ path: '/admin/catalog_forecast/market/' + brandZone.zone.periodHasZones[0].id })"
                        >
                            <span class="pastille" :class="getStatusStyle(brandZone.zone.periodHasZones[0])"></span>
                            <span>{{ brandZone.zone.name }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <RechallengeMarkets
                v-if="rechallengeMarketsParams"
                :params="rechallengeMarketsParams"
                @success="changePeriod(selectedPeriod, false)"
                @close="rechallengeMarketsParams = null"
            ></RechallengeMarkets>
        </div>
        <div class="no-period d-flex align-items-center justify-content-center" v-else>{{ $t("catalogForecast.noPeriod") }}</div>
    </section>
</template>
<script>
import { FUNCTIONALITY, PERIOD_STATUS } from "@/components/catalogForcast/constants";
import periodService from "@/services/cfPeriod";
import zoneGroupService from "@/services/zoneGroup";
import { moment } from "@/utils/moment";
import Loader from "@/components/loader/loader";
import RechallengeMarkets from "@/components/catalogForcast/elements/RechallengeMarkets";
import PeriodDates from "@/components/catalogForcast/elements/PeriodDates";
import { APPLICATION } from "@/components/admin/brand/constant";

export default {
    components: {
        PeriodDates,
        RechallengeMarkets,
        Loader
    },
    data() {
        return {
            APPLICATION,
            FUNCTIONALITY,
            loading: false,
            isLineProgress: true,
            selectedPeriod: null,
            selectedDate: this.getDate(moment(), "YYYY-MM", "month"),
            offsetControl: 0,
            periods: [],
            zoneGroups: [],
            rechallengeMarketsParams: null
        };
    },
    computed: {
        periodTitle() {
            return (
                "PERIOD " +
                moment(this.selectedPeriod.stampCode, "YYYYMM")
                    .startOf("month")
                    .format("MMM YYYY")
                    .toUpperCase()
            );
        },
        zoneGroupsFiltered() {
            return [...this.zoneGroups]
                .sort((zg1, zg2) => {
                    return zg1.name.toLowerCase() > zg2.name.toLowerCase() ? 1 : -1;
                })
                .map(zg => {
                    const brandZonesDashboard = zg.brandZonesDashboard
                        .filter(bzd => bzd.zone.periodHasZones && bzd.zone.periodHasZones.length)
                        .map(bzd => ({
                            ...bzd,
                            zone: {
                                ...bzd.zone,
                                periodHasZones: bzd.zone.periodHasZones.filter(phz => phz.period.id === this.selectedPeriod.id)
                            }
                        }))
                        .filter(bzd => bzd.zone.periodHasZones.length)
                        .sort((bzd1, bzd2) => {
                            return bzd1.zone.name.toLowerCase() > bzd2.zone.name.toLowerCase() ? 1 : -1;
                        });
                    return { ...zg, brandZonesDashboard };
                });
        },
        currentStamp() {
            return localStorage.getItem("currentStamp");
        },
        isBetweenOpeningAndFirmorderDates() {
            return (
                moment().unix() >= moment(this.selectedPeriod.openingDate).unix() &&
                moment().format("YYYY-MM-DD") <= moment(this.selectedPeriod.firmorderClosingDate).format("YYYY-MM-DD")
            );
        },
        canRechallenge() {
            return this.selectedPeriod.isOpened && this.isBetweenOpeningAndFirmorderDates;
        }
    },
    methods: {
        getDate(date, format, start = "second") {
            return moment(date, "YYYYMM")
                .startOf("month")
                .format(format)
                .toUpperCase();
        },
        updateOffset(value) {
            if (this.offsetControl + value >= 0 && this.offsetControl + 8 + value <= this.periods.length) {
                this.offsetControl = this.offsetControl + value;
            }
        },
        async changePeriod(period, reload = true) {
            this.loading = true;
            this.selectedPeriod = period;
            localStorage.setItem("currentStamp", this.selectedPeriod.stampCode);
            if (reload) {
                this.zoneGroups = [];
            }
            this.zoneGroups = await zoneGroupService.listByPeriodForDashboard(this.selectedPeriod.id);
            this.loading = false;
        },
        getStatusStyle(periodHasZone) {
            switch (periodHasZone.status) {
                case PERIOD_STATUS.INITIAL:
                    return "";
                case PERIOD_STATUS.STARTED:
                    return "started";
                case PERIOD_STATUS.SUBMITTED:
                    return "submitted";
                case PERIOD_STATUS.RECHALLENGED:
                    return "rechallenged";
                default:
                    return "";
            }
        },
        getValidatedNb(brandZonesDashboard) {
            let nb = 0;
            brandZonesDashboard.map(bzd => bzd.zone.periodHasZones.map(phz => (phz.status === PERIOD_STATUS.SUBMITTED ? nb++ : null)));
            return nb;
        },
        getGlobalValidatePercent() {
            let nbTotal = 0;
            let nbValidated = 0;
            this.zoneGroupsFiltered.forEach(zg => {
                nbValidated += this.getValidatedNb(zg.brandZonesDashboard);
                nbTotal += zg.brandZonesDashboard.length;
            });
            return Math.round((nbValidated / nbTotal) * 100 * 10) / 10;
        },
        openModalRechallengeMarkets(brandZonesDashboard) {
            if (brandZonesDashboard.length) {
                this.rechallengeMarketsParams = {
                    periodId: this.selectedPeriod.id,
                    brandZones: brandZonesDashboard,
                    isActive: true
                };
            }
        },
        openModalAllRechallengeMarkets() {
            const brandZonesDashboard = [];
            this.zoneGroupsFiltered.map(zg => brandZonesDashboard.push(...zg.brandZonesDashboard));
            this.openModalRechallengeMarkets(brandZonesDashboard);
        }
    },
    async created() {
        this.loading = true;
        this.periods = await periodService.list();
        if (this.periods && this.periods.length) {
            let currentIndexMonthPeriod = null;
            if (this.currentStamp) {
                currentIndexMonthPeriod = this.periods.findIndex(p => p.stampCode === this.currentStamp);
            } else {
                currentIndexMonthPeriod = this.periods.findIndex(
                    p => this.getDate(p.openingDate, "YYYYMM", "month") === this.getDate(moment(), "YYYYMM", "month")
                );
            }
            if (currentIndexMonthPeriod > -1) {
                if (this.periods.length > 8) {
                    this.offsetControl = currentIndexMonthPeriod - 7;

                    // Si la période sauvegardée fait partie des 8 dernières périodes, on affiche les 8 dernière périodes
                    if (currentIndexMonthPeriod <= this.periods.length - 1 && currentIndexMonthPeriod > this.periods.length - 7) {
                        this.offsetControl = this.periods.length - 8;
                    }

                    if (this.offsetControl < 0) this.offsetControl = 0;
                }
                await this.changePeriod(this.periods[currentIndexMonthPeriod]);
            } else {
                // On sélectionne la dernière période de la liste
                await this.changePeriod(this.periods[this.periods.length - 1]);
                this.offsetControl = this.periods.length - 8;
            }
        }
        this.loading = false;
    }
};
</script>
<style lang="scss" scoped>
.catalog-forecast-dashboard {
    background-color: #fff;
    margin-top: 30px;
    padding: 30px;
    &-header {
        div.disable-arrow {
            color: lightgray;
            cursor: default;

            span {
                cursor: default;
            }
        }

        > div {
            display: flex;
            justify-content: center;

            span {
                cursor: pointer;
            }

            span:not(.control) {
                position: relative;
                border: 1px solid #6f6f6f;
                border-radius: 3px;
                padding: 5px 10px;
                color: #6f6f6f;
                font-size: 22px;
                @media (max-width: 1440px) {
                    font-size: 18px;
                }

                &:hover,
                &.active {
                    color: #fff;
                    background-color: #6f6f6f;
                }

                &.open {
                    &:not(:hover):not(.active) {
                        color: #90bb8a;
                        border: 1px solid #90bb8a;
                    }

                    &:before {
                        content: "";
                        position: absolute;
                        bottom: -1px;
                        right: -1px;
                        border-style: solid;
                        border-width: 10px 10px 10px 10px;
                        border-color: transparent #00c24e #00c24e transparent;
                    }
                }
            }
        }
    }

    &-recap {
        .text-global-progress {
            span {
                font-size: 18px;
                color: #707070;
            }
        }
        .btn-global-rechallenge {
            span {
                font-size: 16px;
            }
        }
    }

    &-title {
        span {
            font-family: "oswald", Sans-Serif;
            font-size: 36px;
            font-weight: bold;
            text-transform: capitalize;
            @media (max-width: 1440px) {
                font-size: 28px;
            }
        }
    }

    &-period-title {
        font-family: "oswald", Sans-Serif;
        font-size: 28px;
        @media (max-width: 1440px) {
            font-size: 24px;
        }
    }

    &-item {
        padding-left: 40px;
        padding-right: 40px;
        &:nth-child(3n + 1) {
            padding-left: 15px;
        }
        &:nth-child(3n + 3) {
            padding-right: 15px;
        }
        .dashboard-header {
            span {
                font-family: "oswald", Sans-Serif;
                font-size: 26px;
                text-transform: initial;
            }
        }
        .dashboard-lines {
            -webkit-box-shadow: 5px 6px 20px -10px rgba(0, 0, 0, 0.75);
            -moz-box-shadow: 5px 6px 20px -10px rgba(0, 0, 0, 0.75);
            box-shadow: 5px 6px 20px -10px rgba(0, 0, 0, 0.75);
            .dashboard-line {
                border-top: 1px solid #bbb;
                border-left: 1px solid #bbb;
                border-right: 1px solid #bbb;
                &:last-child {
                    border-bottom: 1px solid #bbb;
                }
                span {
                    color: #6f6f6f;
                    font-size: 18px;
                    text-transform: initial;
                }
                .pastille {
                    margin: 2px 15px;
                    display: inline-block;
                    border: 1px solid #bbb;
                    border-radius: 100%;
                    width: 30px;
                    height: 30px;
                    &.submitted {
                        border-color: #9fc83b;
                        background-color: #9fc83b;
                    }
                    &.started {
                        position: relative;
                        &:after {
                            content: "";
                            height: 28px;
                            width: 14px; // 2*height
                            background: #9fc83b;
                            border-top-right-radius: 104px; // 4*height
                            border-bottom-right-radius: 104px;
                            position: absolute;
                            left: 14px;
                        }
                    }
                    &.rechallenged {
                        border-color: #f9c53c;
                        background-color: #f9c53c;
                    }
                }
            }
        }
    }
    .no-period {
        font-size: 20px;
    }
}
</style>
<style lang="scss">
.line-progress {
    .vm-progress-bar {
        position: relative;
        .vm-progress-bar__outer {
            border-radius: 0 !important;
            &:after {
                content: "";
                z-index: 0;
                border: 3px solid #ece8e8;
                border-left: none;
                position: absolute;
                height: 100%;
                width: 100%;
            }
            .vm-progress-bar__inner {
                border-radius: 0 !important;
                z-index: 1;
            }
        }
    }
    &.zero-percent {
        .vm-progress-bar__outer {
            &:after {
                border: 3px solid #ece8e8;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// CatalogForecastDashboard.vue?1233cb15